import {defineStore} from "pinia";
import Customer from "../customer/interfaces/Customer";
import {LoyaltyCardMembership} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import {
    attachmentApi,
    customerApi,
    customerProgramApi,
    drugInteractionApi,
    loyaltyCardMembershipApi,
    programApi
} from "@/container";
import {VidalAllergy, VidalPathology} from "@/customer/interfaces/Patient";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import {Pathway, SupportProgram} from "@/program/interfaces/Program";
import ConsumerKeyService from "@/consumer/services/ConsumerKeyService";
import {CustomerDeclaredInformation} from "@/customer/interfaces/DeclaredInformation";
import {Attachment} from "@/attachment/interfaces/attachment";
import {Pet} from "@/customer/interfaces/Pet";

/**
 * The Patient store that stores the customer snapshot and loyalty card memberships.
 */
export const usePatientStore = defineStore('patient', {
    state: () => ({
        customer: null as Customer | null,
        memberships: null as LoyaltyCardMembership[] | null,
        attachments: [] as Attachment[],
        loading: false as boolean | number
    }),
    getters: {
        /**
         * Retrieve a consumer from the customer store for a given consumer key.
         *s
         * @param state
         */
        getConsumer: (state) => {
            return (consumerKey: string): Customer | Beneficiary | undefined => {
                if (!state.customer) {
                    return undefined;
                }

                if (!ConsumerKeyService.isBeneficiary(consumerKey)) {
                    return state.customer;
                } else {
                    return state.customer.beneficiaries.find(beneficiary => beneficiary.consumer_key === consumerKey);
                }
            };
        }
    },
    actions: {
        reset() {
            this.customer = null;
            this.memberships = null;
            this.attachments = [];
        },
        async loadPathway(supportProgramId: number, consumerKey: string, skipDelay = false) {
            return programApi()
                .pathway(supportProgramId, consumerKey, skipDelay)
                .then((pathway: Pathway) => {
                    if (ConsumerKeyService.isBeneficiary(consumerKey)) {
                        const beneficiary = this.customer?.beneficiaries.find(b => b.consumer_key === consumerKey);
                        const index = beneficiary?.programs.map(p => p.programmable_id).indexOf(supportProgramId);

                        (beneficiary!.programs[index as number].programmable as SupportProgram).pathway = pathway;
                    } else {
                        const index = this.customer?.programs.map(p => p.programmable_id).indexOf(supportProgramId);

                        (this.customer!.programs[index as number].programmable as SupportProgram).pathway = pathway;
                    }
                });
        },
        async loadMemberships(customerId: number) {
            return await loyaltyCardMembershipApi()
                .get(customerId)
                .then((memberships: LoyaltyCardMembership[]) => this.memberships = memberships)
                .catch();
        },

        async loadAttachments(consumerKey: string) {
            return await attachmentApi().consumerIndex(consumerKey, ["compliance"])
                .then((attachments: Attachment[]) => {
                    this.attachments = attachments;
                });
        },

        /**
         * Patch the customer profile with declared information, allergies, and pathologies.
         *
         * It update the customer store if the patched customer is the current one.
         */
        async patchCustomerProfile(
            customerId: number,
            declaredInformation: Partial<CustomerDeclaredInformation>,
            allergies?: VidalAllergy[],
            pathologies?: VidalPathology[]
        ) {

            if (Object.keys(declaredInformation).length > 0) {
                await customerApi().patch(customerId, declaredInformation);
            }

            if (allergies && pathologies) {
                await drugInteractionApi().storeProfile(customerId, allergies, pathologies);
            }

            if (customerId === this.customer?.id) {
                const updatedCustomer = await customerApi().read(customerId);

                this.customer = {
                    ...this.customer,
                    declared_information: updatedCustomer.declared_information,
                    vidal_pathologies: updatedCustomer.vidal_pathologies,
                    vidal_allergies: updatedCustomer.vidal_allergies
                };
            }
        },
        /** Update a pet with new data. */
        async updatePet(pet: Pet): Promise<void> {
            if (this.customer!.pets.find(p => p.id === pet.id)) {
                this.customer!.pets.forEach((p, index) => {
                    if (p.id === pet.id) {
                        this.customer!.pets[index] = pet;
                    }
                });

                return;
            }

            this.customer!.pets.push(pet);
        },
        /** delete a pet in the store. */
        async deletePet(pet: Pet): Promise<void> {
            if (this.customer!.pets.find(p => p.id === pet.id)) {
                this.customer!.pets.forEach((p, index) => {
                    if (p.id === pet.id) {
                        this.customer!.pets.splice(index, 1);
                    }
                });
            }
        },
        /**
         * Loads the customer snapshot with programs and beneficiaries programs.
         * @param customerId
         * @param refresh
         */
        async load(customerId: number, refresh: boolean = false): Promise<void> {
            if (this.loading === customerId) {
                return;
            }

            if (this.customer?.id === customerId && !refresh) {
                return;
            }

            let customer: Customer;

            if (refresh) {
                this.loading = false;
            } else {
                this.reset();
                this.loading = customerId;
            }

            return await customerApi()
                .read(customerId)
                .then(customerResponse => {
                    customer = {...customerResponse};
                })
                .then(() => customerProgramApi().snapshot('c_' + customerId))
                .then(programs => {
                    customer.programs = programs;
                    if (programs.length) {
                        this.loadAttachments(`c_${customerId}`);
                        Promise.all(customer.beneficiaries.map((beneficiary: Beneficiary) => {
                            return this.loadAttachments('b_' + beneficiary.id);
                        }));
                    }
                    return Promise.all(customer.beneficiaries.map((beneficiary: Beneficiary) => {
                        return customerProgramApi().snapshot('b_' + beneficiary.id);
                    }));
                })
                .then(programs => {
                    customer.beneficiaries.forEach((beneficiary: Beneficiary, index: number) => {
                        beneficiary.programs = programs[index];
                    });
                })
                .then(() => {
                    this.customer = customer;
                })
                .finally(() => this.loading = false);
        },
        /**
         * Refreshes the customer snapshot.
         */
        async refresh() {
            if (!this.customer) {
                return Promise.reject();
            }

            await this.load(this.customer.id, true);
        },

        /**
         * Refreshed only the customer data without reloading programs snapshot.
         *
         * Keeps existing program for both customer and beneficiaries.
         */
        async refreshCustomer() {
            if (!this.customer) {
                return Promise.reject();
            }

            const customerId = this.customer.id;
            const existingPrograms = this.customer.programs;
            const existingBeneficiaryPrograms = this.customer.beneficiaries.map(beneficiary => {
                return {
                    id: beneficiary.id,
                    programs: beneficiary.programs
                };
            });

            return await customerApi()
                .read(customerId)
                .then(customerResponse => {
                    customerResponse.programs = existingPrograms;
                    customerResponse.beneficiaries.forEach((beneficiary: Beneficiary) => {
                        const existing = existingBeneficiaryPrograms.find(b => b.id === beneficiary.id);
                        if (existing) {
                            beneficiary.programs = existing.programs;
                        }
                    });

                    this.customer = customerResponse;
                });
        }
    }
});
